// 격리소 패널 — /admin/quarantine 실데이터 + 삭제/재검사/허용 실제 API 호출
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUI } from '../lib/uiStore';
import { adminGet, adminPost, adminDelete } from '../lib/adminApi';
import { QUARANTINE_DATA } from '../lib/seedData';

interface QuarantineEntry {
  id: string;
  fileId: string;
  scanJobId: string | null;
  storagePath: string;
  filename?: string | null;
  threatLabel: string | null;
  threatLevel: number | null;
  engineSource: string | null;
  reasonDetail: string | null;
  resolved: boolean;
  createdAt: string;
}

const LEVELS = ['', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const LEVEL_COLORS = ['', 'var(--accent)', 'var(--yellow)', '#ff8c3d', 'var(--red)'];

type Action = 'delete' | 'rescan' | 'allow';
const ACTION_TITLE: Record<Action, string> = {
  delete: '🗑 격리 파일 삭제',
  rescan: '🔄 격리 파일 재검사',
  allow: '✓ 예외 처리 허용',
};

// seedData → QuarantineEntry 변환 (초기 폴백용)
function seedToEntry(row: typeof QUARANTINE_DATA[number]): QuarantineEntry {
  return {
    id: row.id,
    fileId: row.fid,
    scanJobId: null,
    storagePath: `/quarantine/seed/${row.fid}.quarantined`,
    filename: row.name,
    threatLabel: row.threat,
    threatLevel: row.threatLevel,
    engineSource: row.engine,
    reasonDetail: row.reason,
    resolved: false,
    createdAt: row.time,
  };
}

export function Quarantine() {
  const { showToast, openModal } = useUI();
  const [items, setItems] = useState<QuarantineEntry[]>(() => QUARANTINE_DATA.map(seedToEntry));
  const [loading, setLoading] = useState(true);
  const [allDeleted, setAllDeleted] = useState(false);
  // 재검사 중인 항목 fileId set
  const rescanningRef = useRef<Set<string>>(new Set());

  const load = useCallback(() => {
    adminGet<{ items: QuarantineEntry[] }>('/admin/quarantine')
      .then((d) => {
        setItems(d.items.filter((r) => !r.resolved));
        setAllDeleted(false);
      })
      .catch(() => undefined) // 실패 시 seed data 유지
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function pollRescanResult(entry: QuarantineEntry) {
    if (!entry.fileId) return;
    rescanningRef.current.add(entry.fileId);
    const MAX = 15;
    let attempts = 0;
    const tick = () => {
      attempts++;
      adminGet<{ status: string }>(`/admin/scan/${entry.fileId}`)
        .then((data) => {
          if (data.status === 'pending' || data.status === 'scanning') {
            if (attempts < MAX) setTimeout(tick, 2000);
            return;
          }
          rescanningRef.current.delete(entry.fileId);
          if (data.status === 'quarantined') {
            showToast(`${entry.threatLabel ?? entry.fileId} — 위협 확인됨`, 'error');
            load();
          } else {
            showToast(`${entry.fileId} — 안전 확인됨`, 'success');
            setItems((prev) => prev.filter((i) => i.id !== entry.id));
          }
        })
        .catch(() => { rescanningRef.current.delete(entry.fileId); });
    };
    setTimeout(tick, 2000);
  }

  function handleAction(entry: QuarantineEntry, action: Action) {
    const name = entry.threatLabel ?? entry.fileId;
    const desc =
      action === 'delete' ? `<strong>${entry.fileId}</strong>를 영구 삭제합니다. 복구 불가능합니다.`
        : action === 'rescan' ? `<strong>${entry.fileId}</strong>를 최신 DB로 재검사합니다.`
          : `<strong>${entry.fileId}</strong>를 안전 판정하여 예외 처리합니다.`;
    openModal({
      title: ACTION_TITLE[action],
      sub: desc,
      body: action === 'delete' ? '<div>삭제 후 동일 해시 파일은 자동 차단됩니다.</div>' : '<div>처리 후 격리 목록에서 제거됩니다.</div>',
      confirmLabel: '확인',
      onConfirm: () => {
        adminPost(`/admin/quarantine/${entry.id}/action`, { action, operator_id: '__admin__' })
          .then(() => {
            if (action === 'delete') {
              setItems((prev) => prev.filter((i) => i.id !== entry.id));
              showToast(`${name} — 영구 삭제 완료`, 'success');
            } else if (action === 'rescan') {
              showToast(`${name} — 재검사 시작`, 'info');
              pollRescanResult(entry);
            } else {
              setItems((prev) => prev.filter((i) => i.id !== entry.id));
              showToast(`${name} — 허용 — webhook 발송됨`, 'success');
            }
          })
          .catch(() => showToast('처리 실패', 'error'));
      },
    });
  }

  function handleBulkDelete() {
    if (items.length === 0) return;
    const count = items.length;
    openModal({
      title: '🗑 격리 파일 전체 삭제',
      sub: `<strong>${count}개</strong>의 격리 파일을 모두 영구 삭제합니다.`,
      body: '<div>이 작업은 복구 불가능합니다.</div>',
      confirmLabel: '확인',
      onConfirm: () => {
        adminDelete('/admin/quarantine')
          .then(() => {
            setItems([]);
            setAllDeleted(true);
            showToast(`${count}개 전체 삭제 완료`, 'success');
          })
          .catch(() => showToast('삭제 실패', 'error'));
      },
    });
  }

  return (
    <>
      <div className="sect-head">
        <div className="sect-title">🔒 격리소 <em>의심 파일 운영자 확인</em></div>
        <button className="btn btn-danger btn-sm" onClick={handleBulkDelete} disabled={items.length === 0}>전체 삭제</button>
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>로딩 중...</div>}

      {!loading && items.length > 0 && (
        <div className="q-alert">
          <div className="q-alert-icon">⚠</div>
          <div className="q-alert-text">
            <strong>{items.length}개의 파일이 격리되어 있으며 운영자 확인이 필요합니다.</strong>
            <br />격리 파일은 별도 보안 디렉터리에 보관됩니다.
          </div>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text3)' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>✓</div>
          <div style={{ fontSize: 13 }}>{allDeleted ? '모두 처리되었습니다' : '격리된 파일이 없습니다'}</div>
        </div>
      )}

      <div id="qList">
        {items.map((entry) => (
          <QuarantineCard key={entry.id} entry={entry} onAction={(a) => handleAction(entry, a)} />
        ))}
      </div>
    </>
  );
}

function QuarantineCard({ entry, onAction }: { entry: QuarantineEntry; onAction: (a: Action) => void }) {
  const level = Math.min(entry.threatLevel ?? 2, 4);
  const filename = entry.filename ?? entry.storagePath.split('/').pop() ?? entry.fileId;

  return (
    <div className="q-card">
      <div className="q-card-head">
        <div className="file-icon fi-danger" style={{ width: 42, height: 42, fontSize: 18 }}>⛔</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{filename}</div>
          <div className="text-mono text-muted" style={{ marginTop: 3 }}>{entry.fileId} · {new Date(entry.createdAt).toLocaleString('ko-KR')}</div>
        </div>
        <div className="threat-level">
          <div className="threat-bars">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={`threat-bar ${i <= level ? 'on lv' + level : ''}`} />
            ))}
          </div>
          <span className="threat-label" style={{ color: LEVEL_COLORS[level] }}>{LEVELS[level]}</span>
        </div>
      </div>
      <div className="q-card-body">
        <div>
          <div className="q-field-label">탐지 엔진</div>
          <div className="q-field-val">{entry.engineSource ?? '-'}</div>
        </div>
        <div>
          <div className="q-field-label">위협 레이블</div>
          <div className="q-field-val" style={{ color: 'var(--red)' }}>{entry.threatLabel ?? '-'}</div>
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <div className="q-field-label">분석 내용</div>
          <div className="q-field-val full">{entry.reasonDetail ?? '-'}</div>
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <div className="q-field-label">저장 경로</div>
          <div className="q-field-val mono" style={{ fontSize: 12, wordBreak: 'break-all' }}>{entry.storagePath}</div>
        </div>
      </div>
      <div className="q-card-foot">
        <button className="btn btn-danger btn-sm" onClick={() => onAction('delete')}>🗑 삭제</button>
        <button className="btn btn-blue btn-sm" onClick={() => onAction('rescan')}>🔄 재검사</button>
        <button className="btn btn-green btn-sm" onClick={() => onAction('allow')}>✓ 허용 (예외 처리)</button>
      </div>
    </div>
  );
}
